import { createBrowserRouter, RouterProvider, Navigate, useParams } from "react-router";
import { useEffect, useRef } from "react";

import 'assets/reset.css'
import { useWebview } from "hooks/useWebview";
import { useKeyboardInset } from "hooks/useKeyboardInset";
import { AlertProvider } from "contexts/AlertContext";
import { ProtectedRoute } from "components/common/ProtectedRoute";
import { ChatNotificationLayout } from "components/common/chatNotification/ChatNotificationLayout";
import { Header } from "components/common/header/Header";
import { useMeetingDetail } from "hooks/useMeeting";
import { meetingStore } from "store/meetingStore";
import { Onboarding } from "pages/onboarding";
// SignUp은 SSO(wiz-sso-front)에서 처리하므로 제거
import { Login } from "pages/login";
import { Home } from "pages/home";
import { Functions } from "pages/functions";
import { MeetingTypeSelect } from "pages/meeting/create/MeetingTypeSelect";
import { MeetingCreateStep1 } from "pages/meeting/create/Step1";
import { MeetingCreateStep2 } from "pages/meeting/create/Step2";
import { TravelPlanStep } from "pages/meeting/create/TravelPlanStep";
import { TravelDestination } from "pages/meeting/create/TravelDestination";
import { TravelGatherPlace } from "pages/meeting/create/TravelGatherPlace";
import { PlaceExplore } from "pages/meeting/create/PlaceExplore";
import { SubPlaceRecommend } from "pages/meeting/SubPlaceRecommend";
import { ThemeRecommend } from "pages/meeting/create/ThemeRecommend";
import { PlaceSearch } from "pages/meeting/create/PlaceSearch";
import { TimePicker } from "pages/meeting/create/TimePicker";
import { MeetingShare } from "pages/meeting/create/MeetingShare";
import { TravelMeetingShare } from "pages/meeting/create/TravelMeetingShare";
import { TravelRecommendations } from "pages/meeting/create/TravelRecommendations";
import { JoinWithCode } from "pages/meeting/JoinWithCode";

import { MeetingDetail } from "pages/meeting/MeetingDetail";
import { MeetingDetailV6 } from "pages/meeting/detail-v6/MeetingDetailV6";
import { MeetingMap } from "pages/meeting/MeetingMap";
import { RealtimeLocation } from "pages/meeting/RealtimeLocation";
import { MeetingEdit } from "pages/meeting/MeetingEdit";
import { SharedPlace } from "pages/meeting/SharedPlace";
import { SafeReturnStart } from "pages/meeting/SafeReturnStart";
import { SafeReturnWatchRequest } from "pages/meeting/SafeReturnWatchRequest";
import { SafeReturnSession } from "pages/meeting/SafeReturnSession";
import { HomeAddress } from "pages/profile/HomeAddress";
import { InvitationEntry } from "pages/invitation/InvitationEntry";
import { InvitationInfo } from "pages/invitation/InvitationInfo";
import { InvitationResult } from "pages/invitation/InvitationResult";
import { GuestLogin } from "pages/invitation/GuestLogin";
import { ScheduleMain } from "pages/schedule/ScheduleMain";
import { ScheduleInput } from "pages/schedule/ScheduleInput";
import { ScheduleVote } from "pages/schedule/ScheduleVote";
import { PlaceVote } from "pages/schedule/PlaceVote";
import { ScheduleResult } from "pages/schedule/ScheduleResult";
import { ScheduleSelectMeeting } from "pages/schedule/ScheduleSelectMeeting";
import { CostInput } from "pages/cost/CostInput";
import { CostReceipt } from "pages/cost/CostReceipt";
import { CostResult } from "pages/cost/CostResult";
import { CostShare } from "pages/cost/CostShare";
import { CostPublic } from "pages/cost/CostPublic";
import { ChatEmbed } from "pages/chat";
import { Settings } from "pages/settings";
import { Profile } from "pages/settings/Profile";
import { BlockedUsers } from "pages/settings/BlockedUsers";
import { Notifications } from "pages/settings/Notifications";
import { ChangePassword } from "pages/settings/ChangePassword";
import { MyLocationViews } from "pages/settings/MyLocationViews";
import { MeetingsList } from "pages/meeting/MeetingsList";
import { FlashCreate } from "pages/flash/FlashCreate";
import { FlashShare } from "pages/flash/FlashShare";
import { FlashStatus } from "pages/flash/FlashStatus";
import { FlashVote } from "pages/flash/FlashVote";
import { NotFound } from "pages/notFound";
// 디자인 시스템 쇼케이스 (개발 확인용, 서비스 라우트 아님)
import { Showcase } from "pages/ds/Showcase";
import { HomePreview } from "pages/ds/HomePreview";
import { MeetingPreview } from "pages/ds/MeetingPreview";
import { UpcomingPreview } from "pages/ds/UpcomingPreview";
import { FinishedPreview } from "pages/ds/FinishedPreview";
import { OverlayPreview } from "pages/ds/OverlayPreview";
import { SponsoredPreview } from "pages/ds/SponsoredPreview";
import { PlaceReviewPreview } from "pages/ds/PlaceReviewPreview";
import { TravelCoursePreview } from "pages/ds/TravelCoursePreview";
import { TravelJournalPreview } from "pages/ds/TravelJournalPreview";
import { TravelTracePreview } from "pages/ds/TravelTracePreview";
// [임시:NATIVE-MAP-PREVIEW] 확인 끝나면 이 import 와 아래 라우트를 지웁니다
import { NativeMapPreview } from "pages/ds/NativeMapPreview";
import { PlacePhotosPage } from "pages/place/PlacePhotosPage";
import { MeetingJournalPage } from "pages/meeting/MeetingJournalPage";
import { TravelCourseDetail } from "pages/travel/TravelCourseDetail";

// /meeting/:meetingId를 /meeting/detail/:meetingId로 리다이렉트하는 컴포넌트
const MeetingDetailRedirect = () => {
    const { meetingId } = useParams<{ meetingId: string }>();
    return <Navigate to={`/meeting/detail/${meetingId}`} replace />;
};

/**
 * 모임 상세 진입 스위치 — meetingType 로 v6 vs 기존 상세 자동 분기.
 *  - TRAVEL (여행 모임) → MeetingDetailV6 (Day timeline · dot-line stepper · 관광지 리스트)
 *  - 그 외 (DAY 모임/정기모임 등) → 기존 MeetingDetail
 * 강제 오버라이드용 우회 URL:
 *  - /meeting/detail-v6/:id  → 무조건 v6
 *  - /meeting/detail-legacy/:id → 무조건 기존
 */
const MeetingDetailSwitch = () => {
    const { meetingId: raw } = useParams<{ meetingId: string }>();
    const meetingId = typeof raw === 'string' ? raw : undefined;
    const { data: meetingData, status } = useMeetingDetail();

    // meetingId 별로 결정한 컴포넌트를 lock — 결정 후 하위 컴포넌트가 재fetch(status=pending 재진입) 를
    // 유발해도 여기서 skeleton 으로 돌아가지 않도록 방어. 안 그러면 v6/기존 언마운트→cleanup(onDefault)
    // →다시 default→다시 fetch→pending 무한 flash loop 발생.
    const decidedRef = useRef<{ meetingId: string; kind: 'v6' | 'legacy' } | null>(null);

    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
        }
    }, [meetingId]);

    // meetingId 가 바뀌면 결정 초기화
    if (decidedRef.current && decidedRef.current.meetingId !== meetingId) {
        decidedRef.current = null;
    }

    // 최초 로드가 끝났으면 kind 확정
    if (!decidedRef.current && meetingId && status === 'ready' && meetingData) {
        // useMeetingDetail().data 는 { data: {...실제모임...}, meta } 형태 wrap 되어 있음
        const meeting = (meetingData as any)?.data || meetingData;
        const isTravel = meeting?.meetingType === 'TRAVEL';
        decidedRef.current = { meetingId, kind: isTravel ? 'v6' : 'legacy' };
    }

    if (!meetingId) return <Navigate to="/home" replace />;

    if (decidedRef.current) {
        return decidedRef.current.kind === 'v6' ? <MeetingDetailV6 /> : <MeetingDetail />;
    }

    // 아직 결정 전 (최초 로드 대기) — Header 만 있는 스켈레톤
    return (
        <div style={{ backgroundColor: 'var(--t-surface-sunken)', minHeight: '100dvh' }}>
            <Header title="" isSubPage />
        </div>
    );
};

// /meeting/invite/:inviteCode → /invitation/:inviteCode 리다이렉트 (딥링크 대응)
const InvitationRedirect = () => {
    const { inviteCode } = useParams<{ inviteCode: string }>();
    return <Navigate to={`/invitation/${inviteCode?.toUpperCase()}`} replace />;
};

const router = createBrowserRouter([{
    element: <ChatNotificationLayout />,
    children: [
    // ===== Public Routes (인증 불필요) =====
    { path: "/onboarding", element: <Onboarding /> },
    { path: "/login", element: <Login /> },
    // 회원가입은 SSO에서 처리 - /login으로 리다이렉트
    { path: "/sign-up", element: <Navigate to="/login" replace /> },
    // 초대 링크는 로그인 없이도 볼 수 있도록 (초대 정보 확인 후 로그인 유도)
    { path: "/invitation/:inviteCode", element: <InvitationEntry /> },
    { path: "/invitation/info/:inviteCode", element: <InvitationInfo /> },
    { path: "/invitation/result/:inviteCode", element: <InvitationResult /> },
    // 딥링크 대응: /meeting/invite/xxx → /invitation/xxx로 리다이렉트
    { path: "/meeting/invite/:inviteCode", element: <InvitationRedirect /> },
    // 게스트 로그인 (이미 참여한 비회원 → 이름+전화번호로 인증)
    { path: "/guest-login/:meetingId", element: <GuestLogin /> },
    // 공개 정산 결과 (공유 링크, 인증 불필요)
    { path: "/cost/public/:meetingId", element: <CostPublic /> },
    // 급번개 투표 (공유 링크, 인증 불필요)
    { path: "/flash/vote/:token", element: <FlashVote /> },
    // 급번개 후보 제안 — 추천장소 화면 재사용 (인증 불필요)
    { path: "/flash/add-place/:token", element: <PlaceExplore /> },

    // ===== Protected Routes (인증 필요) =====
    { path: "/", element: <ProtectedRoute><Home /></ProtectedRoute> },
    { path: "/home", element: <ProtectedRoute><Home /></ProtectedRoute> },
    { path: "/functions", element: <ProtectedRoute><Functions /></ProtectedRoute> },

    // 모임 생성
    // Step1: 모임유형 → Step2: 모임정보+일정 → Step3: 장소(탐색/스킵)
    { path: "/meeting/create", element: <ProtectedRoute><MeetingTypeSelect /></ProtectedRoute> },
    { path: "/meeting/create/step1", element: <ProtectedRoute><MeetingTypeSelect /></ProtectedRoute> },
    { path: "/meeting/create/step2", element: <ProtectedRoute><MeetingCreateStep2 /></ProtectedRoute> },
    { path: "/meeting/create/theme-recommend", element: <ProtectedRoute><ThemeRecommend /></ProtectedRoute> },
    { path: "/meeting/create/step3", element: <ProtectedRoute><PlaceExplore /></ProtectedRoute> },
    { path: "/meeting/create/travel-step3", element: <ProtectedRoute><TravelDestination /></ProtectedRoute> },
    { path: "/meeting/create/travel-step4", element: <ProtectedRoute><TravelGatherPlace /></ProtectedRoute> },
    { path: "/meeting/create/travel", element: <ProtectedRoute><TravelPlanStep /></ProtectedRoute> },
    { path: "/meeting/create/travel-destination", element: <ProtectedRoute><TravelDestination /></ProtectedRoute> },
    { path: "/meeting/create/place-explore", element: <ProtectedRoute><PlaceExplore /></ProtectedRoute> },
    { path: "/meeting/sub-place-recommend", element: <ProtectedRoute><SubPlaceRecommend /></ProtectedRoute> },
    { path: "/meeting/place-search", element: <ProtectedRoute><PlaceSearch /></ProtectedRoute> },
    { path: "/meeting/time-picker", element: <ProtectedRoute><TimePicker /></ProtectedRoute> },
    { path: "/meeting/share", element: <ProtectedRoute><MeetingShare /></ProtectedRoute> },
    { path: "/meeting/travel-share", element: <ProtectedRoute><TravelMeetingShare /></ProtectedRoute> },
    { path: "/meeting/travel-recommendations", element: <ProtectedRoute><TravelRecommendations /></ProtectedRoute> },

    // 모임 목록 및 상세
    { path: "/join-code", element: <ProtectedRoute><JoinWithCode /></ProtectedRoute> },
    { path: "/my-meetings", element: <Navigate to="/meetings" replace /> },
    { path: "/meetings", element: <ProtectedRoute><MeetingsList /></ProtectedRoute> },
    // /meeting/detail/:id → 여행 모임이면 v6, 아니면 기존 상세 (자동 분기)
    { path: "/meeting/detail/:meetingId", element: <ProtectedRoute><MeetingDetailSwitch /></ProtectedRoute> },
    // 강제 오버라이드 우회 URL
    { path: "/meeting/detail-v6/:meetingId", element: <ProtectedRoute><MeetingDetailV6 /></ProtectedRoute> },
    { path: "/meeting/detail-legacy/:meetingId", element: <ProtectedRoute><MeetingDetail /></ProtectedRoute> },
    { path: "/meeting/:meetingId/map", element: <ProtectedRoute><MeetingMap /></ProtectedRoute> },
    { path: "/meeting/location/:meetingId", element: <ProtectedRoute><RealtimeLocation /></ProtectedRoute> },
    { path: "/meeting/edit/:meetingId", element: <ProtectedRoute><MeetingEdit /></ProtectedRoute> },
    { path: "/meeting/shared-place", element: <ProtectedRoute><SharedPlace /></ProtectedRoute> },
    { path: "/meeting/:meetingId", element: <ProtectedRoute><MeetingDetailRedirect /></ProtectedRoute> },

    // 안전 귀가 (Safe Return Helper)
    { path: "/profile/home-address", element: <ProtectedRoute><HomeAddress /></ProtectedRoute> },
    { path: "/safe-return/start/:meetingId", element: <ProtectedRoute><SafeReturnStart /></ProtectedRoute> },
    { path: "/safe-return/watch/:sessionId", element: <ProtectedRoute><SafeReturnWatchRequest /></ProtectedRoute> },
    { path: "/safe-return/session/:sessionId", element: <ProtectedRoute><SafeReturnSession /></ProtectedRoute> },

    // 일정
    { path: "/schedule/select", element: <ProtectedRoute><ScheduleSelectMeeting /></ProtectedRoute> },
    { path: "/schedule/main/:meetingId", element: <ProtectedRoute><ScheduleMain /></ProtectedRoute> },
    { path: "/schedule/input/:meetingId", element: <ProtectedRoute><ScheduleInput /></ProtectedRoute> },
    { path: "/schedule/vote/:meetingId", element: <ProtectedRoute><ScheduleVote /></ProtectedRoute> },
    /* 장소 후보 투표 — 시간 투표와 따로. 묶으면 "시간은 좋은데 장소는 싫다" 를
       표현할 수 없고 후보 수가 곱으로 늘어납니다. */
    { path: "/schedule/place-vote/:meetingId", element: <ProtectedRoute><PlaceVote /></ProtectedRoute> },
    { path: "/schedule/result/:meetingId", element: <ProtectedRoute><ScheduleResult /></ProtectedRoute> },

    // 급번개
    { path: "/flash/create", element: <ProtectedRoute><FlashCreate /></ProtectedRoute> },
    { path: "/flash/share/:token", element: <ProtectedRoute><FlashShare /></ProtectedRoute> },
    { path: "/flash/status/:flashId", element: <ProtectedRoute><FlashStatus /></ProtectedRoute> },

    // 정산
    { path: "/cost/:meetingId", element: <ProtectedRoute><CostInput /></ProtectedRoute> },
    { path: "/cost/receipt/:meetingId", element: <ProtectedRoute><CostReceipt /></ProtectedRoute> },
    { path: "/cost/result/:meetingId", element: <ProtectedRoute><CostResult /></ProtectedRoute> },
    { path: "/cost/share/:meetingId", element: <ProtectedRoute><CostShare /></ProtectedRoute> },

    // 채팅
    { path: "/chat", element: <ProtectedRoute><ChatEmbed /></ProtectedRoute> },
    { path: "/chat/*", element: <ProtectedRoute><ChatEmbed /></ProtectedRoute> },

    // 설정
    { path: "/settings", element: <ProtectedRoute><Settings /></ProtectedRoute> },
    { path: "/settings/profile", element: <ProtectedRoute><Profile /></ProtectedRoute> },
    { path: "/settings/blocked-users", element: <ProtectedRoute><BlockedUsers /></ProtectedRoute> },
    { path: "/settings/notifications", element: <ProtectedRoute><Notifications /></ProtectedRoute> },
    { path: "/settings/change-password", element: <ProtectedRoute><ChangePassword /></ProtectedRoute> },
    { path: "/settings/my-location-views", element: <ProtectedRoute><MyLocationViews /></ProtectedRoute> },

    // 디자인 시스템 쇼케이스 — 인증 없이 접근 가능한 개발 확인용 화면
    { path: "/ds", element: <Showcase /> },
    { path: "/ds/home", element: <HomePreview /> },
    { path: "/ds/meeting", element: <MeetingPreview /> },
    { path: "/ds/upcoming", element: <UpcomingPreview /> },
    { path: "/ds/finished", element: <FinishedPreview /> },
    { path: "/ds/overlays", element: <OverlayPreview /> },
    { path: "/ds/sponsored", element: <SponsoredPreview /> },
    { path: "/ds/place-review", element: <PlaceReviewPreview /> },
    { path: "/ds/travel-course", element: <TravelCoursePreview /> },
    { path: "/ds/travel-journal", element: <TravelJournalPreview /> },
    { path: "/ds/travel-trace", element: <TravelTracePreview /> },
    // [임시:NATIVE-MAP-PREVIEW]
    { path: "/ds/native-maps", element: <NativeMapPreview /> },
    { path: "/place/:placeId/photos", element: <ProtectedRoute><PlacePhotosPage /></ProtectedRoute> },
    { path: "/meeting/:meetingId/journal", element: <ProtectedRoute><MeetingJournalPage /></ProtectedRoute> },
    { path: "/travel/course/:courseId", element: <ProtectedRoute><TravelCourseDetail /></ProtectedRoute> },

    // ===== Catch-all (404) — must be last =====
    { path: "*", element: <NotFound />, errorElement: <NotFound /> }
    ]
}])

export default function Router() {

    useWebview();
    /* 키보드가 덮는 높이를 --t-keyboard-inset 에 적어 둡니다 —
       화면 아래에 붙는 시트가 그만큼 올라갑니다. */
    useKeyboardInset();

    // 네이티브 딥링크 → SPA 라우팅 처리
    useEffect(() => {
        const navigateSafe = (rawPath: string) => {
            let path = rawPath;
            // 절대 URL 방어 — pathname + search + hash 만 추출
            try {
                if (/^https?:\/\//i.test(path)) {
                    const u = new URL(path);
                    path = `${u.pathname}${u.search}${u.hash}`;
                }
            } catch {
                // 파싱 실패 시 원본 사용
            }
            router.navigate(path);
        };

        const handleAppNavigate = (e: Event) => {
            const path = (e as CustomEvent).detail?.path;
            if (path) {
                navigateSafe(path);
            }
        };
        window.addEventListener('app-navigate', handleAppNavigate);

        // Router 마운트 전 도착해 sessionStorage 에 큐잉된 NAVIGATE 가 있으면 flush
        try {
            const pending = sessionStorage.getItem('pending_navigate');
            if (pending) {
                sessionStorage.removeItem('pending_navigate');
                // 다음 tick 에 navigate — 라우터 초기 렌더 안정화 후 진행
                setTimeout(() => navigateSafe(pending), 0);
            }
        } catch {
            // storage 실패 무시
        }

        // /meeting/create/* 흐름 빠져나갔을 때 deferred_navigate(=safe-return 점프 보류) flush
        // 사용자가 모임 생성 도중 push 받은 safe-return 점프를 작업 완료 후에야 처리
        const checkDeferred = () => {
            try {
                const deferred = sessionStorage.getItem('deferred_navigate');
                if (!deferred) return;
                if (window.location.pathname.startsWith('/meeting/create')) return;
                sessionStorage.removeItem('deferred_navigate');
                setTimeout(() => navigateSafe(deferred), 0);
            } catch {
                // ignore
            }
        };
        // pop/push 모두 감지하기 위해 popstate + 직접 polling 둘 다 사용 (react-router 내부 navigate 는 popstate 이벤트 안 쏨)
        window.addEventListener('popstate', checkDeferred);
        const deferredPoll = setInterval(checkDeferred, 1000);

        return () => {
            window.removeEventListener('app-navigate', handleAppNavigate);
            window.removeEventListener('popstate', checkDeferred);
            clearInterval(deferredPoll);
        };
    }, []);

    return (
        <AlertProvider>
            <RouterProvider router={router} />
        </AlertProvider>
    );
}